/** Pure text helpers for the ordered-list field (no editor/React imports). */

/**
 * The list field renders its own `<ol>` numbering, so leading list markers in
 * pasted text ("1. ", "2) ", "- ", "• ") would double up ("1. 1. …"). Strip
 * them per line; the line's position in the list supplies the number.
 */
export const stripLeadingListMarker = (line) => line.replace(/^\s*(?:\d+[.)]|[-*+•])\s+/, "");

/**
 * Plain-text rendering of the list field WITH its numbering ("1. …" markers,
 * one item per line) — the markdown ordered-list dialect. Used by the Draft
 * scenes Copy button so the copied text reads (and re-parses) as a numbered
 * list anywhere it lands; generateText strips the visual numbering.
 */
export const getNumberedTextFromTiptapListField = (json) => {
  if (!json || typeof json !== "object" || !Array.isArray(json.content)) return "";
  const orderedList = json.content.find((node) => node.type === "customOrderedList");
  if (!orderedList?.content || !Array.isArray(orderedList.content)) return "";

  const itemTexts = orderedList.content
    .filter((item) => item.type === "customListItem")
    .map((item) =>
      (Array.isArray(item.content) ? item.content : [])
        .filter((c) => c && c.type === "text" && typeof c.text === "string")
        .map((c) => c.text)
        .join("")
        .trim(),
    )
    .filter((text) => text.length > 0);

  return itemTexts.map((text, i) => `${i + 1}. ${text}`).join("\n");
};

/**
 * text/plain clipboard flavor for Cmd+C selection copies inside the list
 * field editor. Copying puts text/html (a real <ol>) on the clipboard too,
 * which is why rich targets (Word, Docs, Obsidian) keep the numbering — but
 * plain targets (Cursor, Codex, terminals) read text/plain, where TipTap's
 * default serializer flattens items to bare lines separated by blank lines.
 * Emit "1. " markers instead when the selection spans multiple items; a
 * partial or single-item selection stays bare text.
 */
export const serializeListFieldSliceToNumberedText = (slice) => {
  const itemTexts = [];
  const collectItems = (node) => {
    if (node.type?.name === "customListItem") {
      itemTexts.push(node.textContent.trim());
      return;
    }
    node.forEach?.((child) => collectItems(child));
  };
  slice.content.forEach((node) => collectItems(node));

  const nonEmpty = itemTexts.filter((text) => text.length > 0);
  if (nonEmpty.length < 2) {
    return slice.content.textBetween(0, slice.content.size, "\n");
  }
  return nonEmpty.map((text, i) => `${i + 1}. ${text}`).join("\n");
};
